import { useState, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarIcon, Calculator, AlertCircle, Info, ChevronDown, CalendarCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { useHolidays } from '@/hooks/useHolidays';
import type { 
  Deadline, 
  CreateDeadlineInput, 
  DeadlineCategory,
  DeadlinePriority,
  DeadlineDayType
} from '@/types/deadlines';
import { CATEGORY_LABELS, PRIORITY_LABELS, DAY_TYPE_LABELS } from '@/types/deadlines';

const formSchema = z.object({
  title: z.string().min(3, 'El título debe tener al menos 3 caracteres'),
  description: z.string().optional(),
  expediente_number: z.string().optional(),
  interested_party: z.string().optional(),
  date_mode: z.enum(['fixed', 'auto']).default('fixed'),
  start_date: z.date().optional(),
  deadline_days: z.number().min(1).optional(),
  due_date: z.date().optional(),
  category: z.enum(['hacienda', 'contratacion', 'urbanismo', 'personal', 'otros']),
  priority: z.enum(['alta', 'media', 'baja']),
  day_type: z.enum(['habiles', 'naturales', 'fecha_fija']),
  is_critical: z.boolean().default(false),
  legal_reference: z.string().optional(),
  reminder_7: z.boolean().default(true),
  reminder_3: z.boolean().default(true),
  reminder_1: z.boolean().default(true),
  reminder_0: z.boolean().default(true),
}).refine((data) => {
  if (data.date_mode === 'auto') {
    return data.start_date && data.deadline_days && data.deadline_days > 0;
  }
  return data.due_date;
}, {
  message: 'Debes indicar la fecha límite o usar la calculadora de plazos',
  path: ['due_date'],
});

type FormValues = z.infer<typeof formSchema>;

interface DeadlineFormProps {
  deadline?: Deadline;
  onSubmit: (data: CreateDeadlineInput) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export function DeadlineForm({
  deadline,
  onSubmit,
  onCancel,
  isLoading = false,
}: DeadlineFormProps) {
  const { calculateDeadlineDate, autonomousCommunity } = useHolidays();
  const [calculatedDate, setCalculatedDate] = useState<Date | null>(null);
  const [originalDate, setOriginalDate] = useState<Date | null>(null);
  const [wasAdjusted, setWasAdjusted] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(
    // Open advanced by default if editing and has advanced fields filled
    !!(deadline?.description || deadline?.legal_reference || deadline?.is_critical)
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: deadline?.title || '',
      description: deadline?.description || '',
      expediente_number: deadline?.expediente_number || '',
      interested_party: deadline?.interested_party || '',
      date_mode: 'fixed',
      start_date: undefined,
      deadline_days: undefined,
      due_date: deadline?.due_date ? new Date(deadline.due_date) : undefined,
      category: deadline?.category || 'otros',
      priority: deadline?.priority || 'media',
      day_type: deadline?.day_type || 'habiles',
      is_critical: deadline?.is_critical || false,
      legal_reference: deadline?.legal_reference || '',
      reminder_7: deadline?.reminder_days?.includes(7) ?? true,
      reminder_3: deadline?.reminder_days?.includes(3) ?? true,
      reminder_1: deadline?.reminder_days?.includes(1) ?? true,
      reminder_0: deadline?.reminder_days?.includes(0) ?? true,
    },
  });

  // Watch fields
  const dateMode = useWatch({ control: form.control, name: 'date_mode' });
  const startDate = useWatch({ control: form.control, name: 'start_date' });
  const deadlineDays = useWatch({ control: form.control, name: 'deadline_days' });
  const dayType = useWatch({ control: form.control, name: 'day_type' });
  const isCritical = useWatch({ control: form.control, name: 'is_critical' });

  // Auto-enable close reminders when marked as critical
  useEffect(() => {
    if (isCritical) {
      form.setValue('reminder_1', true);
      form.setValue('reminder_0', true);
    }
  }, [isCritical, form]);

  // Calculate date when calculator inputs change
  useEffect(() => {
    const calculate = async () => {
      if (dateMode !== 'auto' || !startDate || !deadlineDays || deadlineDays < 1) {
        setCalculatedDate(null);
        setOriginalDate(null);
        setWasAdjusted(false);
        return;
      }

      setIsCalculating(true);
      try {
        const result = await calculateDeadlineDate(startDate, deadlineDays, dayType);
        setCalculatedDate(result.dueDate);
        setOriginalDate(result.originalDueDate);
        setWasAdjusted(result.wasAdjusted);
      } catch (error) {
        console.error('Error calculating date:', error);
      } finally {
        setIsCalculating(false);
      }
    };

    calculate();
  }, [dateMode, startDate, deadlineDays, dayType, calculateDeadlineDate]);

  const handleSubmit = async (values: FormValues) => {
    const reminderDays: number[] = [];
    if (values.reminder_7) reminderDays.push(7);
    if (values.reminder_3) reminderDays.push(3);
    if (values.reminder_1) reminderDays.push(1);
    if (values.reminder_0) reminderDays.push(0);

    let finalDueDate: Date;
    let finalOriginalDate: Date;

    if (values.date_mode === 'auto' && calculatedDate && originalDate) {
      finalDueDate = calculatedDate;
      finalOriginalDate = originalDate;
    } else if (values.due_date) {
      finalDueDate = values.due_date;
      finalOriginalDate = values.due_date;
    } else {
      return;
    }

    await onSubmit({
      title: values.title,
      description: values.description || undefined,
      expediente_number: values.expediente_number || undefined,
      interested_party: values.interested_party || undefined,
      due_date: finalDueDate.toISOString().split('T')[0],
      original_due_date: finalOriginalDate.toISOString().split('T')[0],
      category: values.category,
      priority: values.priority,
      day_type: values.day_type,
      is_critical: values.is_critical,
      legal_reference: values.legal_reference || undefined,
      reminder_days: reminderDays,
    });
  };

  const communityLabel = autonomousCommunity === 'andalucia' ? 'Andalucía' : autonomousCommunity === 'extremadura' ? 'Extremadura' : 'ámbito nacional';

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
        {/* ── Sección 1: Datos del expediente ── */}
        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Datos del expediente
          </legend>

          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Título del expediente *</FormLabel>
                <FormControl>
                  <Input placeholder="Ej: Licencia de obra C/ Mayor 15" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="expediente_number"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nº Expediente</FormLabel>
                  <FormControl>
                    <Input placeholder="Ej: EXP-2026/001234" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="interested_party"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Interesado</FormLabel>
                  <FormControl>
                    <Input placeholder="Nombre del interesado" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Categoría</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar categoría" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="priority"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Prioridad</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar prioridad" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>
        </fieldset>

        {/* ── Sección 2: Fecha límite (radio buttons) ── */}
        <fieldset className="space-y-3">
          <legend className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            Fecha límite
          </legend>

          <FormField
            control={form.control}
            name="date_mode"
            render={({ field }) => (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => field.onChange('fixed')}
                  className={cn(
                    "flex items-center gap-2 p-3 rounded-lg border-2 text-left transition-all text-sm",
                    field.value === 'fixed'
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/30"
                  )}
                >
                  <CalendarCheck className={cn("h-4 w-4 flex-shrink-0", field.value === 'fixed' ? "text-primary" : "text-muted-foreground")} />
                  <div>
                    <p className="font-medium">Fecha fija</p>
                    <p className="text-xs text-muted-foreground">Seleccionar fecha directamente</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => field.onChange('auto')}
                  className={cn(
                    "flex items-center gap-2 p-3 rounded-lg border-2 text-left transition-all text-sm",
                    field.value === 'auto'
                      ? "border-primary bg-primary/5"
                      : "border-border hover:border-muted-foreground/30"
                  )}
                >
                  <Calculator className={cn("h-4 w-4 flex-shrink-0", field.value === 'auto' ? "text-primary" : "text-muted-foreground")} />
                  <div>
                    <p className="font-medium">Calcular</p>
                    <p className="text-xs text-muted-foreground">Festivos de {communityLabel}</p>
                  </div>
                </button>
              </div>
            )}
          />

          {/* Fixed date mode */}
          {dateMode === 'fixed' && (
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="due_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha límite *</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                          >
                            {field.value ? (
                              format(field.value, 'PPP', { locale: es })
                            ) : (
                              <span>Seleccionar fecha</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="day_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de cómputo</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar tipo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.entries(DAY_TYPE_LABELS).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
            </div>
          )}

          {/* Auto-calculate mode */}
          {dateMode === 'auto' && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <FormField
                  control={form.control}
                  name="start_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Fecha de inicio *</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              className={cn(
                                'w-full pl-3 text-left font-normal',
                                !field.value && 'text-muted-foreground'
                              )}
                            >
                              {field.value ? (
                                format(field.value, 'dd/MM/yy', { locale: es })
                              ) : (
                                <span>Inicio</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            initialFocus
                            className="pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="deadline_days"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nº de días *</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          placeholder="Ej: 10"
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="day_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de días</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Tipo" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="habiles">Días hábiles</SelectItem>
                          <SelectItem value="naturales">Días naturales</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormItem>
                  )}
                />
              </div>

              {/* Calculation result */}
              {isCalculating ? (
                <div className="p-3 bg-muted rounded-lg animate-pulse">
                  <p className="text-sm text-muted-foreground">Calculando fecha...</p>
                </div>
              ) : calculatedDate ? (
                <div className={cn(
                  "p-3 rounded-lg border",
                  wasAdjusted ? "bg-deadline-soon/10 border-deadline-soon/30" : "bg-deadline-safe/10 border-deadline-safe/30"
                )}>
                  <div className="flex items-start gap-2">
                    {wasAdjusted ? (
                      <AlertCircle className="h-4 w-4 text-deadline-soon flex-shrink-0 mt-0.5" />
                    ) : (
                      <CalendarIcon className="h-4 w-4 text-deadline-safe flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-sm">
                        Fecha límite: {format(calculatedDate, "EEEE d 'de' MMMM 'de' yyyy", { locale: es })}
                      </p>
                      {wasAdjusted && originalDate && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Ajustada desde {format(originalDate, "d MMM", { locale: es })} por festivo/fin de semana
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : startDate && deadlineDays ? (
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm text-muted-foreground">Introduce los datos para calcular</p>
                </div>
              ) : null}
            </div>
          )}
        </fieldset>

        {/* ── Sección 3: Configuración avanzada (colapsable) ── */}
        <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 w-full text-sm font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors"
            >
              <ChevronDown className={cn("h-4 w-4 transition-transform", advancedOpen && "rotate-180")} />
              Configuración avanzada
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3 pt-3">
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Añade detalles relevantes sobre el expediente..."
                      className="resize-none"
                      rows={2}
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="legal_reference"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Referencia legal</FormLabel>
                  <FormControl>
                    <Input placeholder="Ej: Art. 21 LPAC" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_critical"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Marcar como crítico</FormLabel>
                    <FormDescription>
                      Los plazos críticos se destacan con un icono de alerta y activan recordatorios cercanos
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <Label>Recordatorios</Label>
              <div className="flex flex-wrap gap-4">
                <FormField
                  control={form.control}
                  name="reminder_7"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="font-normal">7 días</FormLabel>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="reminder_3"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="font-normal">3 días</FormLabel>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="reminder_1"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="font-normal">1 día</FormLabel>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="reminder_0"
                  render={({ field }) => (
                    <FormItem className="flex items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="font-normal">Mismo día</FormLabel>
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Guardando...' : deadline ? 'Actualizar' : 'Crear plazo'}
          </Button>
        </div>
      </form>
    </Form>
  );
}
